import React, { ReactElement } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import { isNil } from "ramda";

import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import IconSelector from "components/shared/images/IconSelector";
import RegistarImage from "components/shared/images/RegistarImage";
import GenericStatus from "components/shared/status/GenericStatus";

import { getDomainsList } from "redux/selectors/domainsSelector";

import ChangeAutomigrationStatus from "../actions/ChangeAutomigrationStatus";
import ResetAuthinfo from "../actions/ResetAuthinfo";
import SendVerificationInList from "../actions/SendVerificationInList";

const DomainsDetails = (): ReactElement => {
 const domainData = useSelector(getDomainsList)[0];

 const emailVerificationMessage = (emailVerified: number): ReactElement => {
  switch (emailVerified) {
   case 0:
    return (
     <Typography variant="caption" color="error">
      <FormattedMessage id="domain.emailVerificationFailed" />
     </Typography>
    );
   case 1:
    return (
     <Typography variant="caption">
      <FormattedMessage id="domain.emailVerificationNeeded" />
     </Typography>
    );
   case 2:
    return (
     <Typography variant="caption">
      <FormattedMessage id="domain.emailVerificationPending" />
     </Typography>
    );
   default:
    return <></>;
  }
 };

 const formatProviderName = (provider: string): string => {
  switch (provider) {
   case "internetbs":
    return "IBS";
   case "ascio":
    return "Ascio";
   default:
    return "";
  }
 };

 return (
  <Paper elevation={1} sx={{ borderRadius: "5px" }}>
   <Stack spacing={1} p={2}>
    <Stack textAlign="left" spacing={1}>
     <Typography fontSize={16} fontWeight="bold" variant="unboundedTitle" textTransform="uppercase">
      {domainData.name}
     </Typography>
     <Stack direction="row" alignItems="center" spacing={1}>
      <RegistarImage provider={domainData.provider} />
      <Typography>{formatProviderName(domainData.provider)}</Typography>
     </Stack>
    </Stack>
    <Divider />
    <Stack>
     <Stack alignItems="center" justifyContent="flex-start" direction="row" spacing={1}>
      <IconSelector icon="DetailsIcon" props={{ fontSize: "small", color: "kxOrange" }} />
      <Typography variant="unboundedTitle" fontSize={14} fontWeight="bold">
       <FormattedMessage id="domains.details" />
      </Typography>
     </Stack>
     <Stack direction="row" alignItems="center" textAlign="start">
      <Typography fontWeight="bold" variant="subtitle2" minWidth={120}>
       <FormattedMessage id="domain.automigrate" />
      </Typography>
      <GenericStatus status={domainData.automigration ? "online" : "offline"} />
     </Stack>
     <Stack direction="row" alignItems="center" textAlign="start">
      <Typography fontWeight="bold" variant="subtitle2" minWidth={120}>
       <FormattedMessage id="domains.authInfo" />
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2}>
       <Typography variant="subtitle2">{domainData.authinfo}</Typography>
      </Stack>
     </Stack>
    </Stack>
    {!isNil(domainData.emailVerified) && domainData.emailVerified !== 3 && (
     <Stack>
      <Divider />
      <Stack alignItems="center" justifyContent="flex-start" direction="row" spacing={1}>
       <IconSelector icon="MailIcon" props={{ fontSize: "medium", color: "kxOrange" }} />
       <Typography variant="unboundedTitle" fontSize={14} fontWeight="bold">
        <FormattedMessage id="domains.email" />
       </Typography>
      </Stack>
      <Stack direction="row" textAlign="left">
       <Typography fontWeight="bold" variant="subtitle2" minWidth={120}>
        <FormattedMessage id="domains.email" />
       </Typography>
       <Typography variant="caption">{domainData.email}</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" textAlign="left">
       <Typography fontWeight="bold" variant="subtitle2" minWidth={120}>
        <FormattedMessage id="domain.status" />
       </Typography>
       <Stack direction="row" alignItems="center" spacing={2}>
        {emailVerificationMessage(domainData.emailVerified)}
       </Stack>
      </Stack>
     </Stack>
    )}
    <Divider />
    <Stack direction="row" justifyContent="space-around">
     <Stack alignItems="center">
      <ResetAuthinfo />
      <Typography variant="extraSmall">
       <FormattedMessage id="domain.generateAuthinfo" />
      </Typography>
     </Stack>
     <Stack alignItems="center">
      <ChangeAutomigrationStatus
       id={domainData.id}
       value={domainData.automigration ? false : true}
      />
      <Typography variant="extraSmall">
       <FormattedMessage id="domain.editAutomigration" />
      </Typography>
     </Stack>
     {!isNil(domainData.emailVerified) && domainData.emailVerified !== 3 && (
      <Stack alignItems="center">
       <SendVerificationInList id={domainData.id} />
       <Typography variant="extraSmall">
        <FormattedMessage id="domain.sendVerification" />
       </Typography>
      </Stack>
     )}
    </Stack>
   </Stack>
  </Paper>
 );
};

export default DomainsDetails;
